import React from "react";
import { Link } from "react-router-dom";
import { Shield, Smartphone, Server, Zap, Lock, GitBranch, ShoppingCart, Layers, Image as ImageIcon, Check, Search, ArrowUpRight } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";

/* ============ MOCKUPS PAR FEATURE ============ */

const BrowserChrome = ({ children, url = "wilink.fr" }) => (
  <div className="rounded-xl border-2 border-border bg-card overflow-hidden shadow-xl">
    <div className="h-9 bg-muted border-b border-border flex items-center gap-2 px-3">
      <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
      <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
      <span className="h-2.5 w-2.5 rounded-full bg-green-400" />
      <div className="ml-3 flex-1 h-5 rounded bg-background border border-border flex items-center px-2 text-[10px] text-muted-foreground font-mono">
        🔒 {url}
      </div>
    </div>
    {children}
  </div>
);

const IconTile = ({ cls, label }) => (
  <div className="flex flex-col items-center gap-1">
    <div className="w-12 h-12 rounded-[22%] bg-primary text-white flex items-center justify-center shadow-md relative overflow-hidden" style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), 0 6px 12px rgba(0,0,0,0.12)" }}>
      <i className={`${cls} text-base`} />
      <span className="absolute inset-0 bg-gradient-to-b from-white/20 to-transparent" />
    </div>
    {label && <span className="text-[9px] text-muted-foreground">{label}</span>}
  </div>
);

const Mock_Portal = () => (
  <BrowserChrome url="portail.macompagnie.fr">
    <div className="p-5 bg-gradient-to-b from-background to-muted/30">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-7 px-3 rounded-full border border-border bg-card flex-1 flex items-center text-[11px] text-muted-foreground"><Search className="h-3 w-3 mr-2" /> Rechercher...</div>
      </div>
      <div className="flex gap-1.5 mb-4 flex-wrap">
        {["TOUT","DEV","COMM","SÉCURITÉ","RÉSEAU"].map((c,i) => (
          <span key={c} className={`text-[9px] px-2 py-0.5 rounded-full border ${i===0?"bg-primary text-white border-primary":"bg-card border-border text-muted-foreground"}`}>{c}</span>
        ))}
      </div>
      <div className="grid grid-cols-6 gap-x-3 gap-y-4">
        {["fa-brands fa-slack","fa-brands fa-github","fa-brands fa-figma","fa-solid fa-cloud","fa-brands fa-discord","fa-solid fa-database","fa-brands fa-aws","fa-solid fa-shield-halved","fa-solid fa-terminal","fa-solid fa-envelope","fa-solid fa-calendar","fa-brands fa-jira"].map((c,i) => (
          <IconTile key={i} cls={c} label={["Slack","GitHub","Figma","Cloud","Discord","Mongo","AWS","VPN","SSH","Mail","Cal","Jira"][i]} />
        ))}
      </div>
    </div>
  </BrowserChrome>
);

const Mock_Security = () => (
  <BrowserChrome url="admin · whitelist">
    <div className="p-6 bg-card space-y-3">
      <div className="flex items-center gap-2 mb-2"><Lock className="h-4 w-4 text-primary" /><strong className="text-sm">Whitelist IP active</strong><span className="ml-auto text-[10px] bg-green-500/10 text-green-600 px-2 py-0.5 rounded-full font-medium">ENABLED</span></div>
      {[
        { ip: "192.168.1.0/24", label: "LAN bureau" },
        { ip: "82.120.45.12",  label: "Domicile dirigeant" },
        { ip: "10.0.0.0/8",     label: "VPN WireGuard" },
      ].map((r,i) => (
        <div key={i} className="flex items-center gap-3 p-2.5 rounded border border-border bg-background">
          <span className="font-mono text-xs flex-1">{r.ip}</span>
          <span className="text-[10px] text-muted-foreground">{r.label}</span>
          <span className="h-2 w-2 rounded-full bg-green-500" />
        </div>
      ))}
      <div className="mt-4 p-3 rounded border-2 border-dashed border-border text-[11px] text-muted-foreground flex items-center gap-2">
        <Shield className="h-4 w-4" /> 2FA TOTP activé pour <code className="text-foreground">admin@netkom.fr</code>
      </div>
    </div>
  </BrowserChrome>
);

const Mock_Admin = () => (
  <BrowserChrome url="admin · liens">
    <div className="bg-card grid grid-cols-[140px_1fr] min-h-[280px]">
      <aside className="bg-muted/40 border-r border-border p-3 text-[11px] space-y-1.5">
        <div className="font-bold uppercase tracking-wider text-muted-foreground text-[9px] mb-1">// Admin</div>
        {["Tableau de bord","Liens","Catégories","Annonces","Whitelist","Réglages","Mise à jour"].map((m,i) => (
          <div key={m} className={`px-2 py-1 rounded ${i===1?"bg-primary text-primary-foreground":"text-muted-foreground"}`}>{m}</div>
        ))}
      </aside>
      <div className="p-4 space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">// 14 LIENS</div>
        {[
          { t: "Télémaintenance", c: "OUTILS", color: "bg-blue-500" },
          { t: "GitHub Org",       c: "DEV",    color: "bg-zinc-700" },
          { t: "Slack Workspace",  c: "COMM",   color: "bg-purple-500" },
          { t: "Vaultwarden",      c: "SÉC",    color: "bg-rose-500" },
        ].map((l,i) => (
          <div key={i} className="flex items-center gap-2 p-2 rounded border border-border bg-background text-xs">
            <span className={`h-6 w-6 rounded ${l.color}`} />
            <span className="flex-1 font-medium">{l.t}</span>
            <span className="text-[9px] uppercase tracking-wider text-muted-foreground">{l.c}</span>
            <span className="text-muted-foreground">⋮⋮</span>
          </div>
        ))}
      </div>
    </div>
  </BrowserChrome>
);

const Mock_Updates = () => (
  <BrowserChrome url="admin · mise à jour">
    <div className="p-6 bg-card">
      <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-5 mb-4">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center"><GitBranch className="h-5 w-5" /></div>
          <div className="flex-1">
            <div className="font-bold text-sm">Mise à jour du code disponible</div>
            <div className="text-xs text-muted-foreground mt-1">Version <strong className="text-foreground">2.4.0</strong> · Sortie le 10/06/2026</div>
          </div>
        </div>
        <button className="mt-4 w-full h-9 rounded bg-primary text-primary-foreground text-xs font-bold">⬇ Mettre à jour maintenant</button>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">// Historique</div>
      {[
        { v: "2.3.1", d: "01/06/2026", note: "Fix wrap labels" },
        { v: "2.3.0", d: "20/05/2026", note: "Sous-catégories + drag" },
      ].map((h,i) => (
        <div key={i} className="flex gap-3 py-1.5 text-xs border-t border-border first:border-0">
          <span className="font-mono font-bold w-12">{h.v}</span>
          <span className="text-muted-foreground w-20">{h.d}</span>
          <span className="text-muted-foreground">{h.note}</span>
        </div>
      ))}
    </div>
  </BrowserChrome>
);

const Mock_Branding = () => (
  <BrowserChrome url="admin · réglages">
    <div className="p-6 bg-card space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Logo mode clair</div>
        <div className="h-14 rounded border border-border bg-muted/30 flex items-center justify-center text-2xl font-black text-primary">YourLogo</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Logo mode sombre</div>
        <div className="h-14 rounded border border-border bg-zinc-900 flex items-center justify-center text-2xl font-black text-white">YourLogo</div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Couleur primaire</div>
        <div className="flex gap-2">
          {["#002FA7","#0066FF","#16A34A","#DC2626","#7C3AED","#F59E0B"].map((c,i) => (
            <div key={c} className={`h-8 w-8 rounded ${i===0?"ring-2 ring-offset-2 ring-primary":""}`} style={{ background: c }} />
          ))}
        </div>
      </div>
    </div>
  </BrowserChrome>
);

const Mock_Hosting = () => (
  <BrowserChrome url="install.php">
    <div className="p-6 bg-card font-mono text-[11px] space-y-1 leading-relaxed">
      <div className="text-green-600">$ ftp votre-hebergement.fr</div>
      <div className="text-muted-foreground">Connected ✓</div>
      <div className="text-green-600">$ upload wilink-php.zip</div>
      <div className="text-muted-foreground">85 KB transferred (2.3s)</div>
      <div className="text-green-600">$ ouvrir https://votredomaine.fr/install.php</div>
      <div className="text-muted-foreground">⚙ Installation guidée en 1 page</div>
      <div className="text-green-600">$ tester</div>
      <div className="text-primary">🎉 Portail WiLink opérationnel.</div>
      <div className="mt-3 pt-3 border-t border-border grid grid-cols-3 gap-2 not-font-mono">
        {["OVH","Ionos","o2switch"].map(h => (
          <div key={h} className="bg-muted/40 rounded text-center py-1.5 text-[10px] font-bold tracking-wider">{h}</div>
        ))}
      </div>
    </div>
  </BrowserChrome>
);

/* ============ SECTIONS ============ */

const SECTIONS = [
  { icon: Smartphone, title: "Page d'accueil style iPhone",
    desc: "Icônes carrées arrondies monochromes, fond personnalisable, recherche instantanée. Une UX moderne familière pour vos collaborateurs.",
    bullets: ["Logo & couleur de marque personnalisables","Sous-catégories à 2 niveaux","Recherche en temps réel","Tooltips au survol (description)"],
    Mock: Mock_Portal },
  { icon: Lock, title: "Sécurité avancée",
    desc: "Whitelist IP, 2FA TOTP (Google Authenticator), CSRF, bcrypt — toute la sécurité d'un produit pro intégrée.",
    bullets: ["Whitelist IP : restreint l'accès à votre LAN/VPN","2FA TOTP optionnel pour les admins","Protection brute-force","Sessions sécurisées"],
    Mock: Mock_Security },
  { icon: Layers, title: "Backoffice complet",
    desc: "Gérez vos liens, catégories, annonces, IP autorisées et tous les réglages depuis une interface admin élégante.",
    bullets: ["Drag-and-drop pour réordonner","Catégories avec couleurs custom","Annonces avec rotation auto","Statistiques de clics par lien"],
    Mock: Mock_Admin },
  { icon: GitBranch, title: "Mises à jour en 1 clic",
    desc: "Depuis votre backoffice, un seul bouton télécharge la dernière version. Vos données et identifiants BDD ne sont jamais touchés.",
    bullets: ["Téléchargement automatique du ZIP","Préservation des credentials BDD","Reset OPcache intégré","Notifications de version disponible"],
    Mock: Mock_Updates },
  { icon: ImageIcon, title: "Personnalisation visuelle",
    desc: "Logo (mode clair et sombre), fond, couleurs, annonces, page d'accès refusé — chaque détail de votre portail vous appartient.",
    bullets: ["Logo dédié clair / sombre","Fond image personnalisable","Mode sombre natif","Couleur d'icône par catégorie"],
    Mock: Mock_Branding },
  { icon: Server, title: "Hébergement ultra léger",
    desc: "PHP 7.4+ / MySQL pur. Aucun framework lourd, aucune dépendance Node ou Docker. Compatible tous hébergements à 3€/mois.",
    bullets: ["OVH, Ionos, o2switch, Hostinger…","Installation guidée en 1 page","Import/Export CSV/JSON","Sources lisibles et modifiables"],
    Mock: Mock_Hosting },
];

const FeatureSection = ({ icon: Icon, title, desc, bullets, Mock, reverse }) => (
  <section className={`max-w-6xl mx-auto px-6 py-16 grid md:grid-cols-2 gap-12 items-center ${reverse ? "md:[direction:rtl]" : ""}`} data-testid={`section-${title}`}>
    <div className="[direction:ltr]">
      <div className="h-12 w-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-5"><Icon className="h-6 w-6" /></div>
      <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">{title}</h2>
      <p className="text-muted-foreground mb-6 leading-relaxed">{desc}</p>
      <ul className="space-y-2 text-sm">
        {bullets.map((b, i) => <li key={i} className="flex items-start gap-2"><Check className="h-4 w-4 text-primary mt-0.5 shrink-0" /><span>{b}</span></li>)}
      </ul>
    </div>
    <div className="[direction:ltr]"><Mock /></div>
  </section>
);

export default function PresentationPage() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="presentation-page">
      <PublicHeader />
      <section className="max-w-4xl mx-auto px-6 pt-20 pb-12 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-4">// Présentation détaillée</p>
        <h1 className="text-5xl sm:text-6xl font-black tracking-tight mb-6">Tout ce que WiLink fait pour vous</h1>
        <p className="text-lg text-muted-foreground">Une visite guidée des fonctionnalités, avec aperçu du backoffice et des cas d&apos;usage.</p>
        <div className="flex flex-wrap justify-center gap-3 mt-8">
          <Link to="/demo" className="h-11 px-5 rounded-md border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-medium inline-flex items-center gap-2 transition-colors"><Zap className="h-5 w-5" /> Voir la démo</Link>
          <Link to="/tarifs" className="h-11 px-5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-medium inline-flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> Commander 69€</Link>
        </div>
      </section>
      <div className="border-t border-border" />
      {SECTIONS.map((s, i) => (
        <React.Fragment key={i}>
          <FeatureSection {...s} reverse={i % 2 === 1} />
          <div className="border-t border-border" />
        </React.Fragment>
      ))}
      <section className="max-w-3xl mx-auto px-6 py-20 text-center">
        <h2 className="text-4xl font-black tracking-tight mb-4">Convaincu ?</h2>
        <p className="text-muted-foreground mb-8">Testez la démo en direct ou commandez votre licence dès maintenant.</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/demo" className="h-12 px-6 rounded-md border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-medium inline-flex items-center gap-2 transition-colors"><Zap className="h-5 w-5" /> Tester la démo</Link>
          <Link to="/tarifs" className="h-12 px-6 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-medium inline-flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> Voir le tarif</Link>
        </div>
      </section>
      <PublicFooter />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css" />
    </div>
  );
}
